import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, Zap, Rocket, Crown } from "lucide-react";
import { useReveal } from "@/lib/useReveal";
import { toast } from "sonner";

const tiers = [
  {
    id: "starter",
    name: "Starter",
    icon: Zap,
    monthly: 9,
    annual: 7,
    tagline: "Build the habit.",
    features: [
      "Full workout library (6 disciplines)",
      "Weekly auto-generated plan",
      "Basic progress tracking",
      "Mobile + web app",
    ],
    cta: "Get Started",
    accent: "cyan",
    featured: false,
  },
  {
    id: "pro",
    name: "Pro",
    icon: Rocket,
    monthly: 19,
    annual: 15,
    tagline: "Train like an athlete.",
    features: [
      "Everything in Starter",
      "Adaptive intensity + RPE tuning",
      "Full progress dashboard + PRs",
      "Coach feedback (24h SLA)",
      "Custom nutrition macros",
    ],
    cta: "Start 14-Day Free Trial",
    accent: "lime",
    featured: true,
  },
  {
    id: "elite",
    name: "Elite",
    icon: Crown,
    monthly: 49,
    annual: 39,
    tagline: "1:1. No compromises.",
    features: [
      "Everything in Pro",
      "Dedicated head coach",
      "Monthly 1:1 video call",
      "Fully custom programming",
      "Priority form reviews",
    ],
    cta: "Apply for Elite",
    accent: "orange",
    featured: false,
  },
];

export const Pricing = () => {
  const [annual, setAnnual] = useState(true);
  const ref = useReveal();

  return (
    <section id="pricing" data-testid="pricing-section" className="relative py-20 sm:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={ref} className="reveal text-center max-w-2xl mx-auto mb-10">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs uppercase tracking-widest text-white/70">
            <Zap className="h-3.5 w-3.5 text-neon-lime" />
            Pricing
          </div>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight text-white">
            CHOOSE YOUR PACE.
            <br />
            <span className="text-neon-lime">CANCEL ANYTIME.</span>
          </h2>
          <p className="mt-3 text-white/70">14-day free trial on Pro. No credit card for Starter.</p>

          <div className="mt-6 inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 p-1">
            <button
              type="button"
              data-testid="pricing-toggle-monthly"
              onClick={() => setAnnual(false)}
              className={`px-4 py-1.5 text-xs rounded-full btn-lift ${!annual ? "bg-white text-ink-0 font-semibold" : "text-white/70"}`}
            >
              Monthly
            </button>
            <button
              type="button"
              data-testid="pricing-toggle-annual"
              onClick={() => setAnnual(true)}
              className={`px-4 py-1.5 text-xs rounded-full btn-lift ${annual ? "bg-neon-lime text-ink-0 font-semibold" : "text-white/70"}`}
            >
              Annual <span className="ml-1 text-[10px] opacity-80">save 20%</span>
            </button>
          </div>
        </div>

        <div className="grid gap-5 lg:grid-cols-3">
          {tiers.map((t) => (
            <TierCard key={t.id} t={t} annual={annual} />
          ))}
        </div>

        <p className="mt-6 text-center text-xs text-white/50">
          Money-back guarantee within 30 days · No hidden fees · Cancel in one tap.
        </p>
      </div>
    </section>
  );
};

const accentCfg = {
  lime: {
    icon: "text-neon-lime bg-neon-lime/10 border-neon-lime/25",
    cta: "bg-neon-lime text-ink-0 hover:bg-neon-lime hover:brightness-95",
    border: "border-neon-lime/35 shadow-glow-lime",
    check: "text-neon-lime",
  },
  cyan: {
    icon: "text-neon-cyan bg-neon-cyan/10 border-neon-cyan/25",
    cta: "bg-white/5 border border-white/10 text-white hover:bg-white/10 hover:border-neon-cyan/40",
    border: "border-white/10",
    check: "text-neon-cyan",
  },
  orange: {
    icon: "text-neon-orange bg-neon-orange/10 border-neon-orange/25",
    cta: "bg-white/5 border border-white/10 text-white hover:bg-white/10 hover:border-neon-orange/40",
    border: "border-white/10",
    check: "text-neon-orange",
  },
};

const TierCard = ({ t, annual }) => {
  const ref = useReveal();
  const a = accentCfg[t.accent];
  const price = annual ? t.annual : t.monthly;
  const period = annual ? "/mo, billed yearly" : "/month";
  const Icon = t.icon;

  const handleCta = () => {
    if (t.id === "starter") toast.success("Welcome to Pulse Starter — no card needed.");
    else if (t.id === "pro") toast.success("Your 14-day Pro trial is ready. Let&rsquo;s go.");
    else toast.success("Elite application received. A coach will reach out within 24h.");
  };

  return (
    <Card
      ref={ref}
      data-testid="pricing-tier-card"
      className={`reveal card-lift bg-white/[0.03] rounded-2xl relative overflow-hidden ${a.border} ${
        t.featured ? "lg:scale-[1.02]" : ""
      }`}
    >
      {t.featured && (
        <div className="absolute top-0 right-0 rounded-bl-xl bg-neon-lime text-ink-0 text-[10px] font-bold uppercase tracking-widest px-3 py-1.5">
          Most Popular
        </div>
      )}
      <CardContent className="p-7">
        <div className="flex items-center gap-3">
          <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl border ${a.icon}`}>
            <Icon className="h-5 w-5" />
          </span>
          <div>
            <div className="font-display text-2xl tracking-wider text-white leading-none">{t.name}</div>
            <div className="text-xs text-white/55 mt-1">{t.tagline}</div>
          </div>
        </div>

        <div className="mt-6 flex items-end gap-1">
          <span className="font-display text-6xl text-white leading-none">${price}</span>
          <span className="text-white/55 text-sm mb-1">{period}</span>
        </div>

        <ul className="mt-6 space-y-3">
          {t.features.map((f) => (
            <li key={f} className="flex items-start gap-2 text-sm text-white/80">
              <Check className={`h-4 w-4 mt-0.5 flex-none ${a.check}`} />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        <Button
          onClick={handleCta}
          data-testid={`pricing-tier-cta-button-${t.id}`}
          className={`btn-lift mt-7 w-full h-11 rounded-xl font-semibold ${a.cta}`}
        >
          {t.cta}
        </Button>
        {t.id === "pro" && <div className="text-center text-[11px] text-white/50 mt-2">No card needed · Cancel anytime</div>}
      </CardContent>
    </Card>
  );
};
